import React, { useRef } from 'react'
import { motion, useMotionValue, useSpring, useTransform } from 'motion/react'
import type { FieldValues } from '../../fieldTypes'

export function ParallaxLayers({ values = {} }: { values?: FieldValues }) {
  const containerRef = useRef<HTMLDivElement>(null)
  const mouseX = useMotionValue(0)
  const mouseY = useMotionValue(0)

  const depth1 = values.depth1 ?? 15
  const depth2 = values.depth2 ?? 30
  const depth3 = values.depth3 ?? 50
  const smoothing = values.smoothing ?? 100

  const springConfig = { stiffness: smoothing, damping: 20 }
  const smoothX = useSpring(mouseX, springConfig)
  const smoothY = useSpring(mouseY, springConfig)

  const x1 = useTransform(smoothX, [-1, 1], [-depth1, depth1])
  const y1 = useTransform(smoothY, [-1, 1], [-depth1, depth1])
  const x2 = useTransform(smoothX, [-1, 1], [-depth2, depth2])
  const y2 = useTransform(smoothY, [-1, 1], [-depth2, depth2])
  const x3 = useTransform(smoothX, [-1, 1], [-depth3, depth3])
  const y3 = useTransform(smoothY, [-1, 1], [-depth3, depth3])

  const handleMouseMove = (e: React.MouseEvent) => {
    const rect = containerRef.current?.getBoundingClientRect()
    if (!rect) return
    const nx = ((e.clientX - rect.left) / rect.width - 0.5) * 2
    const ny = ((e.clientY - rect.top) / rect.height - 0.5) * 2
    mouseX.set(nx)
    mouseY.set(ny)
  }

  const handleMouseLeave = () => {
    mouseX.set(0)
    mouseY.set(0)
  }

  return (
    <div
      ref={containerRef}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      className="h-full flex items-center justify-center relative overflow-hidden cursor-crosshair"
    >
      <div className="relative w-48 h-48">
        <motion.div
          style={{ x: x1, y: y1 }}
          className="absolute inset-0 bg-sky-200/50 rounded-2xl border border-sky-300/40"
        />
        <motion.div
          style={{ x: x2, y: y2 }}
          className="absolute inset-3 bg-indigo-300/50 rounded-2xl border border-indigo-400/40"
        />
        <motion.div
          style={{ x: x3, y: y3 }}
          className="absolute inset-6 bg-violet-400/60 rounded-2xl border border-violet-500/40 flex items-center justify-center"
        >
          <span className="text-white text-xs font-semibold select-none">Move mouse</span>
        </motion.div>
      </div>
    </div>
  )
}
